import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlay, FaClock, FaCheckCircle, FaWhatsapp, FaChevronDown, 
  FaChevronUp, FaCertificate, FaVideo, FaStar, FaUsers,
  FaLock, FaUnlock
} from 'react-icons/fa';
import { MdSchool, MdPlayCircleOutline } from 'react-icons/md';
import { createClient } from '@supabase/supabase-js';

// Configuración de Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY';
const supabase = createClient(supabaseUrl, supabaseKey);

// Número de WhatsApp para consultas
const WHATSAPP_NUMBER = '942128139';

// Datos de cursos con información de venta
const coursesInfo = {
  "seace": {
    id: "seace",
    title: "SEACE - Sistema Electrónico de Contrataciones del Estado",
    subtitle: "Domina las contrataciones públicas mayores a 8 UIT",
    description: "Aprende a navegar y aprovechar todas las oportunidades del SEACE. Desde la búsqueda básica hasta estrategias avanzadas para ganar licitaciones.",
    price: "S/. 150",
    originalPrice: "S/. 300",
    discount: "75% OFF",
    thumbnail: "/seace.png",
    color: "#DC2626",
    duration: "24 horas",
    lessons: 45,
    level: "Básico a Avanzado",
    certificate: true,
    rating: 4.8,
    students: 1250,
    highlights: [
      "Acceso ilimitado de por vida",
      "24 horas de contenido en video",
      "Certificado de finalización",
      "Soporte por WhatsApp"
    ],
    modules: [
      {
        title: "Introducción al SEACE",
        lessons: 9,
        duration: "35min",
        topics: [
          "¿Qué son las contrataciones públicas?",
          "Sistemas de entregas para bienes y servicios",
          "Modalidades de pago",
          "Actores claves de la contratación pública",
          "Impedimentos de contratar con el estado"
        ]
      },
      {
        title: "Procedimientos de Selección",
        lessons: 7,
        duration: "16min",
        topics: [
          "Conceptos generales",
          "Procedimientos competitivos",
          "Topes en procedimientos de selección",
          "Licitaciones públicas",
          "Concursos públicos"
        ]
      },
      {
        title: "Fase de Selección",
        lessons: 8,
        duration: "30min",
        topics: [
          "Actuaciones preparatorias",
          "Bases estándar",
          "Cuestionamiento a las bases",
          "Documentos de admisión",
          "Recursos de apelación",
          "Otorgamiento de la buena pro"
        ]
      },
      {
        title: "Uso Práctico del SEACE",
        lessons: 4,
        duration: "12min",
        topics: [
          "Navegación general del sistema",
          "Búsqueda por nomenclatura",
          "Convocatorias adjudicadas",
          "Inscripción en procedimientos"
        ]
      },
      {
        title: "Casos Prácticos",
        lessons: 4,
        duration: "48min",
        topics: [
          "Evaluación y calificación - Caso 1",
          "Revisión de oferta ganadora - Caso 1",
          "Evaluación y calificación - Caso 2",
          "Revisión de oferta ganadora - Caso 2"
        ]
      }
    ]
  },
  "contratos-menores": {
    id: "contratos-menores",
    title: "Contratos Menores",
    subtitle: "Especialízate en contrataciones menores a 8 UIT",
    description: "Domina completamente los procesos de contratación menores a 8 UIT. Aprende estrategias para ganar más contratos y hacer crecer tu negocio.",
    price: "S/. 197",
    originalPrice: "S/. 297",
    discount: "33% OFF",
    thumbnail: "/cm.png",
    color: "#0066CC",
    duration: "14 horas",
    lessons: 28,
    level: "Básico a Intermedio",
    certificate: true,
    rating: 4.9,
    students: 856,
    highlights: [
      "Acceso de por vida",
      "14 horas de contenido",
      "Certificado incluido",
      "Casos prácticos reales"
    ],
    modules: [
      {
        title: "Fundamentos de Contratos Menores",
        lessons: 8,
        duration: "4h 30min",
        topics: [
          "Introducción a contratos menores",
          "Marco legal vigente",
          "Requisitos y documentación",
          "Proceso de contratación",
          "Plazos y tiempos"
        ]
      },
      {
        title: "Estrategias de Cotización",
        lessons: 10,
        duration: "5h",
        topics: [
          "Cómo preparar una cotización ganadora",
          "Análisis de términos de referencia",
          "Pricing estratégico",
          "Documentos técnicos"
        ]
      },
      {
        title: "Casos Prácticos",
        lessons: 10,
        duration: "4h 30min",
        topics: [
          "Caso 1: Bienes",
          "Caso 2: Servicios",
          "Caso 3: Consultoría",
          "Errores comunes"
        ]
      }
    ]
  },
};

const AcademiaCursos = ({ user }) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [userCourses, setUserCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Cargar cursos del usuario desde Supabase
  useEffect(() => {
    const loadUserCourses = async () => {
      if (!user?.email) {
        console.log('❌ No hay user.email:', user);
        setLoading(false);
        return;
      }

      console.log('🔍 Buscando usuario por email:', user.email);
      console.log('👤 Usuario completo:', user);

      try {
        const { data, error } = await supabase
          .rpc('perfil_portal', { p_correo: user.email });

        console.log('📦 Respuesta de Supabase:');
        console.log('  - data:', data);
        console.log('  - error:', error);

        if (error) {
          console.error('❌ Error de Supabase:', error);
          throw error;
        }

        // Courses puede ser un string como "SEACE,PAC" o un array
        if (data?.Courses) {
          const coursesRaw = data.Courses;
          console.log('📝 Courses (raw):', coursesRaw);
          console.log('📝 Tipo de Courses:', typeof coursesRaw);

          const coursesArray = typeof coursesRaw === 'string' 
            ? coursesRaw.split(',').map(c => c.trim().toLowerCase())
            : Array.isArray(coursesRaw) 
            ? coursesRaw.map(c => c.toLowerCase())
            : [];
          
          console.log('✅ Cursos procesados (array):', coursesArray);
          console.log('🔍 Verificando acceso:');
          console.log('  - SEACE:', coursesArray.includes('seace'));
          console.log('  - PAC:', coursesArray.includes('pac'));
          console.log('  - Contratos Menores:', coursesArray.includes('contratos-menores'));
          
          setUserCourses(coursesArray);
        } else {
          console.log('⚠️ No se encontró el campo Courses o está vacío/null');
          console.log('  - data?.Courses:', data?.Courses);
          setUserCourses([]);
        }
      } catch (error) {
        console.error('❌ Error cargando cursos del usuario:', error);
        setUserCourses([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserCourses();
  }, [user]);

  const hasAccess = (courseId) => {
    return userCourses.includes(courseId.toLowerCase());
  };

  const handleWhatsAppContact = (courseName) => {
    const message = encodeURIComponent(
      `Hola! Estoy interesado en el curso: ${courseName}. ¿Podrían brindarme más información?`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const handleStartCourse = (courseId) => {
    // Mapear los IDs de la academia a los IDs del coursesData
    const courseIdMap = {
      'seace': 'seace',
      'contratos-menores': 'contratos-menores',
      'pac': 'pac'
    };
    
    const mappedId = courseIdMap[courseId] || courseId;
    console.log('Navegando al curso:', mappedId);
    navigate(`/cursos/player/${mappedId}`);
  };

  const toggleModule = (moduleIndex) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleIndex]: !prev[moduleIndex]
    }));
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid #e2e8f0',
            borderTop: '6px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#6b7280' }}>Cargando cursos...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Vista de detalle del curso
  if (selectedCourse) {
    const course = coursesInfo[selectedCourse];
    const hasUserAccess = hasAccess(selectedCourse);

    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        {/* Header con imagen de fondo */}
        <div style={{
          background: `linear-gradient(135deg, ${course.color}dd 0%, ${course.color} 100%)`,
          color: 'white',
          padding: '3rem 2rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: course.thumbnail ? `url(${course.thumbnail})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <button
              onClick={() => setSelectedCourse(null)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '2rem',
                fontWeight: '600',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              ← Volver a Cursos
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem', alignItems: 'start' }}>
              <div>
                {course.discount && (
                  <span style={{
                    background: '#ef4444',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    display: 'inline-block',
                    marginBottom: '1rem'
                  }}>
                    🔥 {course.discount}
                  </span>
                )}
                <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', lineHeight: '1.2' }}>
                  {course.title}
                </h1>
                <p style={{ fontSize: '1.2rem', opacity: 0.9, marginBottom: '2rem' }}>
                  {course.subtitle}
                </p>
                
                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaStar color="#fbbf24" />
                    <span style={{ fontWeight: '600' }}>{course.rating}</span>
                    <span style={{ opacity: 0.8 }}>({course.students} estudiantes)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaClock />
                    <span>{course.duration}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaVideo />
                    <span>{course.lessons} lecciones</span>
                  </div>
                </div>
              </div>

              {/* Card de compra */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
              }}>
                {hasUserAccess ? (
                  <>
                    <div style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      marginBottom: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <FaUnlock />
                      <span style={{ fontWeight: '600' }}>Tienes acceso a este curso</span>
                    </div>
                    <button
                      onClick={() => handleStartCourse(course.id)}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        background: course.color,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 10px 20px ${course.color}66`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <FaPlay />
                      Comenzar Curso
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '2rem', fontWeight: '700', color: course.color }}>
                          {course.price}
                        </span>
                        <span style={{ fontSize: '1.2rem', textDecoration: 'line-through', color: '#9ca3af' }}>
                          {course.originalPrice}
                        </span>
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Pago único - Acceso de por vida</p>
                    </div>

                    <button
                      onClick={() => handleWhatsAppContact(course.title)}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        background: '#25D366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        transition: 'all 0.3s',
                        marginBottom: '1rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(37, 211, 102, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <FaWhatsapp size={24} />
                      Consultar por WhatsApp
                    </button>

                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.75rem', fontWeight: '600' }}>
                        Este curso incluye:
                      </p>
                      {course.highlights.map((highlight, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '0.5rem',
                          fontSize: '0.9rem',
                          color: '#4b5563'
                        }}>
                          <FaCheckCircle color="#10b981" size={16} />
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contenido del curso */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem' }}>
            {/* Descripción y temario */}
            <div>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '2rem',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ marginBottom: '1rem', color: '#1f2937' }}>Lo que aprenderás</h2>
                <p style={{ color: '#6b7280', lineHeight: '1.8', fontSize: '1.05rem' }}>
                  {course.description}
                </p>
              </div>

              {/* Temario */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>
                  Contenido del Curso ({course.modules.length} módulos)
                </h2>

                {course.modules.map((module, index) => (
                  <div key={index} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => toggleModule(index)}
                      style={{
                        padding: '1.25rem',
                        background: expandedModules[index] ? '#f9fafb' : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => {
                        if (!expandedModules[index]) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
                          {module.title}
                        </h3>
                        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                          <span>{module.lessons} lecciones</span>
                          <span>{module.duration}</span>
                        </div>
                      </div>
                      {expandedModules[index] ? <FaChevronUp color="#6b7280" /> : <FaChevronDown color="#6b7280" />}
                    </div>

                    {expandedModules[index] && (
                      <div style={{
                        padding: '1rem 1.25rem 1.25rem',
                        background: '#fafafa',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                          {module.topics.map((topic, topicIndex) => (
                            <li key={topicIndex} style={{
                              padding: '0.75rem 0',
                              borderBottom: topicIndex < module.topics.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '1rem',
                              color: '#4b5563'
                            }}>
                              <MdPlayCircleOutline size={20} color={course.color} />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                position: 'sticky',
                top: '2rem'
              }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>Detalles del Curso</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#6b7280' }}>Nivel:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{course.level}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#6b7280' }}>Duración:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{course.duration}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#6b7280' }}>Lecciones:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>{course.lessons}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#6b7280' }}>Certificado:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaCertificate color="#10b981" />
                      Sí
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Estudiantes:</span>
                    <span style={{ fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaUsers color={course.color} />
                      {course.students}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de lista de cursos
  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '3rem 2rem' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <MdSchool size={48} color="#3b82f6" />
          </div>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#1f2937' }}>
            Academia de Licitaciones Públicas
          </h1>
          <p style={{ fontSize: '1.2rem', color: '#6b7280', maxWidth: '700px', margin: '0 auto' }}>
            Cursos especializados para dominar las contrataciones con el Estado
          </p>
        </div>

        {/* Grid de cursos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '2rem' }}>
          {Object.values(coursesInfo).map((course) => {
            const hasUserAccess = hasAccess(course.id);

            return (
              <div
                key={course.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  border: hasUserAccess ? `3px solid #10b981` : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                }}
              >
                {/* Imagen del curso */}
                <div style={{
                  height: '300px',
                  background: course.thumbnail 
                    ? `url(${course.thumbnail})` 
                    : `linear-gradient(135deg, ${course.color}dd 0%, ${course.color} 100%)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Overlay */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))'
                  }} />

                  {/* Badges */}
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    right: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    zIndex: 2
                  }}>
                    {course.discount && (
                      <span style={{
                        background: '#ef4444',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '700'
                      }}>
                        {course.discount}
                      </span>
                    )}
                    {hasUserAccess && (
                      <span style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FaUnlock size={14} />
                        Acceso Activo
                      </span>
                    )}
                  </div>

                  {/* Botón de play */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(255,255,255,0.95)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s'
                  }}>
                    <FaPlay size={30} color={course.color} />
                  </div>
                </div>

                {/* Contenido */}
                <div style={{ padding: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <FaStar color="#fbbf24" />
                      <span style={{ fontWeight: '600' }}>{course.rating}</span>
                    </div>
                    <span style={{ color: '#9ca3af' }}>|</span>
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      {course.students} estudiantes
                    </span>
                  </div>

                  <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#1f2937' }}>
                    {course.title}
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    {course.subtitle}
                  </p>

                  <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaClock color={course.color} />
                      <span>{course.duration}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaVideo color={course.color} />
                      <span>{course.lessons} lecciones</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaCertificate color={course.color} />
                      <span>Certificado</span>
                    </div>
                  </div>

                  {hasUserAccess ? (
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => handleStartCourse(course.id)}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          background: course.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.boxShadow = `0 8px 16px ${course.color}66`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <FaPlay />
                        Iniciar Curso
                      </button>
                      <button
                        onClick={() => setSelectedCourse(course.id)}
                        style={{
                          padding: '1rem 1.5rem',
                          background: 'white',
                          color: course.color,
                          border: `2px solid ${course.color}`,
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = course.color;
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = course.color;
                        }}
                      >
                        Ver Temario
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ fontSize: '1.8rem', fontWeight: '700', color: course.color }}>
                            {course.price}
                          </span>
                          <span style={{ fontSize: '1.1rem', textDecoration: 'line-through', color: '#9ca3af' }}>
                            {course.originalPrice}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                          onClick={() => handleWhatsAppContact(course.title)}
                          style={{
                            flex: 1,
                            padding: '1rem',
                            background: '#25D366',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 211, 102, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <FaWhatsapp size={20} />
                          Consultar
                        </button>
                        <button
                          onClick={() => setSelectedCourse(course.id)}
                          style={{
                            padding: '1rem 1.5rem',
                            background: 'white',
                            color: course.color,
                            border: `2px solid ${course.color}`,
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = course.color;
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.color = course.color;
                          }}
                        >
                          Ver Detalles
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Garantía */}
        <div style={{
          marginTop: '4rem',
          padding: '2rem',
          background: 'white',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#1f2937' }}>
            ✨ Inversión Segura en tu Desarrollo Profesional
          </h3>
          <p style={{ color: '#6b7280', fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
            Acceso de por vida • Certificado oficial • Soporte por WhatsApp • Actualizaciones gratuitas
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcademiaCursos;